import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import MoreInstanceInfo from './MoreInstanceInfo';
import { instanceLabels, schema } from './utils';
import { INSTANCE_STATE } from '../../../apis/constants';
import { instanceApis } from '../../../apis/instanceApis';
import useToast, { TOAST_TYPE } from '../../../hooks/useToast';
import { isCustomInstance, mapCidrs, sentenceCase } from '../../../utils/utils';
import ActionButton from '../../elements/action-button/ActionButton';
import AlertMessage from '../../elements/alert-message/AlertMessage';
import Text from '../../elements/text/Text';
import TextInput from '../../elements/text-input/TextInput';
import ImageUpload from '../../sections/image-upload/ImageUpload';
import { FILE_SIZE } from '../instance-stepper/constants';
import TabPanel from '../instance-tabs/TabPanel';

const InstanceSettingsTab = ({ instance, onRemoveImage, onUpdateInstance }) => {
  const { showToast } = useToast();
  const { t } = useTranslation();

  const instanceType = instance.clusterType;
  const formLabels = instanceLabels(instanceType);
  const instanceBusy = instance.status !== INSTANCE_STATE.RUNNING;

  const checkDuplicate = async (subdomain) => {
    if (subdomain === instance.subdomain) {
      return { isValid: true };
    }

    try {
      return { isValid: await instanceApis.checkSubdomain(subdomain) };
    } catch (e) {
      showToast(
        TOAST_TYPE.ERROR,
        t('unableToCheckSubdomain', { ns: 'notifications' })
      );

      return { isValid: false };
    }
  };

  const handleUpdateInstance = async (data) => {
    try {
      await onUpdateInstance(data);
      showToast(
        TOAST_TYPE.SUCCESS,
        sentenceCase(
          t('updateSuccess', {
            ns: 'notifications',
            text: t('instanceInfo', { ns: 'instance' }),
          })
        )
      );
    } catch {
      showToast(
        TOAST_TYPE.ERROR,
        sentenceCase(
          t('updateError', {
            ns: 'notifications',
            text: t('instanceInfo', { ns: 'instance' }),
          })
        )
      );
    }
  };

  const handleRemoveImage = async () => {
    try {
      await onRemoveImage();
      showToast(
        TOAST_TYPE.SUCCESS,
        t('deleteSuccess', {
          ns: 'notifications',
          text: t('instanceImage', { ns: 'instance' }),
        })
      );
    } catch (error) {
      showToast(
        TOAST_TYPE.ERROR,
        sentenceCase(
          t('deleteError', {
            ns: 'notifications',
            text: t('instanceImage', { ns: 'instance' }),
          })
        )
      );
    }
  };

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: {
      clusterType: instanceType,
      name: instance.name,
      description: instance.description,
      subdomain: instance.subdomain,
      cidrs: mapCidrs(instance.cidrs),
    },
    mode: 'onChange',
    resolver: yupResolver(schema(checkDuplicate)),
  });

  return (
    <TabPanel value={0} index={0}>
      <Text>{t('settings.instance', { ns: 'descriptions' })}</Text>
      <Box sx={{ mt: 2 }}>
        <MoreInstanceInfo instance={instance} />
      </Box>
      {instanceBusy && (
        <AlertMessage
          message={t('instanceIsBusy', { ns: 'descriptions' })}
          severity="info"
        />
      )}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box sx={{ flex: 1, py: 3 }}>
          <ImageUpload
            name="icon"
            control={control}
            disabled={instanceBusy}
            imageSrc={instance?.icon}
            limit={FILE_SIZE.BYTES}
            onRemoveImage={handleRemoveImage}
          />
        </Box>
        <Box
          sx={{
            '& > div': { mb: 2 },
            flex: 3,
            p: 3,
          }}
        >
          <TextInput
            name="name"
            control={control}
            defaultValue={instance.name}
            disabled={instanceBusy}
            errors={errors}
            label={formLabels.name}
            sx={{ maxWidth: 600 }}
          />
          <TextInput
            name="description"
            control={control}
            disabled={instanceBusy}
            defaultValue={instance.description}
            errors={errors}
            inputProps={{
              'aria-label': 'description',
              maxLength: 256,
            }}
            label={formLabels.description}
            sx={{ maxWidth: 600 }}
          />
          {!isCustomInstance(instanceType) && (
            <TextInput
              name="subdomain"
              control={control}
              defaultValue={instance.subdomain}
              disabled={instanceBusy}
              errors={errors}
              label={formLabels.subdomain}
              sx={{ maxWidth: 600 }}
            />
          )}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <ActionButton
          aria-label="update"
          disabled={!isDirty || !isEmpty(errors) || instanceBusy}
          clickAction={handleSubmit(handleUpdateInstance)}
          sx={{ justifySelf: 'center' }}
        >
          {t('update', { ns: 'buttons' })}
        </ActionButton>
      </Box>
    </TabPanel>
  );
};

InstanceSettingsTab.propTypes = {
  instance: PropTypes.object.isRequired,
  onRemoveImage: PropTypes.func.isRequired,
  onUpdateInstance: PropTypes.func.isRequired,
};

export default InstanceSettingsTab;
